import { useGame } from '../../contexts/GameContext';
import { canAuthorAbilities } from './abilityAuthoringAccess';
import { AbilityAuthoringContent } from './AbilityAuthoringTools';
import React, { useState } from 'react';
import { GameConstants } from '../../../../shared/GameConstants';
import { gameLogger } from '../../../../utils/GameLogger';

interface ProjectileToolsTabProps {
    onAction: (action: string, data?: any) => void;
}

/**
 * ProjectileToolsTab
 *
 * DevTools panel for tuning homing projectile physics in real-time.
 */
export const ProjectileToolsTab: React.FC<ProjectileToolsTabProps> = ({ onAction }) => {
  const [, setRefreshTrigger] = useState(0);
  const { player } = useGame();
  if (!canAuthorAbilities(typeof window === 'undefined' ? '' : window.location.hostname, player?.gmLevel)) return <p>Projectile authoring is available to authorized operators.</p>;

  const handleChange = (key: string, value: number) => {
    (GameConstants.Combat as any)[key] = value;
    setRefreshTrigger(prev => prev + 1);
    onAction('refresh_projectiles');
  };

  // Styles
  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: '12px',
    textTransform: 'uppercase',
    color: '#888',
    marginBottom: '10px',
    marginTop: '20px',
    fontWeight: 'bold',
    letterSpacing: '1px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '11px',
    color: '#aaa',
    marginBottom: '4px'
  };

  const inputStyle: React.CSSProperties = {
    width: '60px',
    padding: '2px 5px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    color: 'white',
    fontSize: '11px'
  };

  const buttonStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    marginBottom: '8px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    color: '#ecf0f1',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '13px',
    transition: 'all 0.2s'
  };

  const sliderParams = [
    { label: 'Basic Homing Strength', key: 'HOMING_STRENGTH_BASIC', min: 0, max: 20, step: 1 },
    { label: 'Special Homing Strength', key: 'HOMING_STRENGTH_SPECIAL', min: 0, max: 20, step: 1 },
    { label: 'Max Turn Rate (°/s)', key: 'HOMING_MAX_TURN_RATE', min: 30, max: 360, step: 10 },
    { label: 'Basic Homing Delay (ms)', key: 'HOMING_DELAY_BASIC', min: 0, max: 500, step: 10 },
    { label: 'Special Homing Delay (ms)', key: 'HOMING_DELAY_SPECIAL', min: 0, max: 500, step: 10 },
    { label: 'Projectile Lifespan (ms)', key: 'PROJECTILE_LIFESPAN', min: 500, max: 5000, step: 100 }
  ];

  return (
    <>
      <details><summary>Ability authoring</summary><AbilityAuthoringContent /></details>
      <div style={sectionHeaderStyle}>Homing Projectiles</div>

      {sliderParams.map(p => (
        <div key={p.key} style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <label style={labelStyle}>{p.label}</label>
            <input
              type="number"
              step={p.step}
              value={(GameConstants.Combat as any)[p.key]}
              style={inputStyle}
              onChange={(e) => handleChange(p.key, Number(e.target.value))}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <input
            type="range" min={p.min} max={p.max} step={p.step}
            value={(GameConstants.Combat as any)[p.key]}
            style={{ width: '100%', margin: 0 }}
            onChange={(e) => handleChange(p.key, Number(e.target.value))}
          />
        </div>
      ))}

      <div style={sectionHeaderStyle}>Quick Presets</div>

      <button
        style={{ ...buttonStyle, background: '#27ae60', borderColor: '#2ecc71' }}
        onClick={() => {
          handleChange('HOMING_STRENGTH_BASIC', 4);
          handleChange('HOMING_STRENGTH_SPECIAL', 8);
          handleChange('HOMING_MAX_TURN_RATE', 200);
          handleChange('HOMING_DELAY_BASIC', 50);
          handleChange('HOMING_DELAY_SPECIAL', 100);
          handleChange('PROJECTILE_LIFESPAN', 1500);
        }}
      >
        🎯 Reset to Defaults
      </button>

      <button
        style={{ ...buttonStyle, background: '#e74c3c', borderColor: '#c0392b' }}
        onClick={() => {
          handleChange('HOMING_STRENGTH_BASIC', 15);
          handleChange('HOMING_STRENGTH_SPECIAL', 20);
          handleChange('HOMING_MAX_TURN_RATE', 360);
          handleChange('HOMING_DELAY_BASIC', 0);
          handleChange('HOMING_DELAY_SPECIAL', 0);
        }}
      >
        🚀 Aggressive Homing (Debug)
      </button>

      <button
        style={{ ...buttonStyle, background: '#3498db', borderColor: '#2980b9' }}
        onClick={() => {
          handleChange('HOMING_STRENGTH_BASIC', 2);
          handleChange('HOMING_STRENGTH_SPECIAL', 4);
          handleChange('HOMING_MAX_TURN_RATE', 90);
          handleChange('HOMING_DELAY_BASIC', 200);
          handleChange('HOMING_DELAY_SPECIAL', 300);
        }}
      >
        🌀 Lazy Missiles (Slow Turn)
      </button>

      <button
        style={{ ...buttonStyle, background: '#e67e22', borderColor: '#d35400', marginTop: '15px' }}
        onClick={() => {
          gameLogger.info('combat', 'Current Projectile Settings:', {
            HOMING_STRENGTH_BASIC: GameConstants.Combat.HOMING_STRENGTH_BASIC,
            HOMING_STRENGTH_SPECIAL: GameConstants.Combat.HOMING_STRENGTH_SPECIAL,
            HOMING_MAX_TURN_RATE: GameConstants.Combat.HOMING_MAX_TURN_RATE,
            HOMING_DELAY_BASIC: GameConstants.Combat.HOMING_DELAY_BASIC,
            HOMING_DELAY_SPECIAL: GameConstants.Combat.HOMING_DELAY_SPECIAL,
            PROJECTILE_LIFESPAN: GameConstants.Combat.PROJECTILE_LIFESPAN
          });
          alert('Projectile settings logged to console!');
        }}
      >
        📋 Export Settings to Console
      </button>
    </>
  );
};
